import { z } from 'zod';

import { ZodRecurringFeeChargePaidBy } from '@Common/types/bo/recurring_fee_charges';
import { buildZodBoListQueryParsed } from '@Common/types/bo/utils/build_zod_bo_list_query';
import { ZodIDPathParam, ZodId } from '@Common/types/request';
import { createZodArrayable } from '@Common/types/zod/zod-arrayable';
import { endpoint, GetEndpointType } from '@Common/types/zod/zod-endpoint';
import { ZodTrimmedString } from '@Common/types/zod/zod-trimmed-string';

export const B2bContractFeeLabelValues = [
  'affiliate_account_closure',
  'affiliate_account_position_at_given_date',
  'affiliate_collective_operation_by_cheque',
  'affiliate_annual_custody_for_former_or_retired_employees',
  'affiliate_annual_custody_for_former_or_retired_employees_pereco',
  'affiliate_undelivered_mail_handling',
  'affiliate_direct_debit_rejection',
  'affiliate_estate_settlement_high_amount',
  'affiliate_estate_settlement_low_amount',
  'affiliate_pledging_or_release_of_assets',
  'affiliate_reissue_bank_transfer',
  'affiliate_repayment_by_cheque',
  'affiliate_specific_mail_requests_processing',
  'affiliate_reissue_cheque',
  'affiliate_repayment_of_savings',
  'affiliate_repayment_reimbursement_triggered',
  'affiliate_retractation_following_pereco_payment',
  'affiliate_search_for_documents_or_transactions_after_10_years',
  'affiliate_search_for_documents_or_transactions_before_10_years',
  'affiliate_statements_and_certificates',
  'affiliate_transfer_from_pee_to_another_manager',
  'affiliate_transfer_from_pereco_to_another_manager_pct',
  'affiliate_voluntary_payment_by_cheque',
  'company_calculating_individual_allocations_particip',
  'company_calculating_individual_allocations_profit',
  'company_ccb_annual_fee',
  'company_ccb_fee_per_account',
  'company_collective_transfer_to_another_manager',
  'company_fee_per_account',
  'company_file_opening',
  'company_integration_of_affiliates_choices_via_mail_particip',
  'company_integration_of_affiliates_choices_via_mail_profit',
  'company_integration_of_affiliates_choices_via_mail_value',
  'company_integration_of_affiliates_consultation_data_particip',
  'company_integration_of_affiliates_consultation_data_profit',
  'company_integration_of_affiliates_consultation_data_value',
  'company_integration_of_affiliates_data',
  'company_other',
  'company_pee_fee_per_account',
  'company_pee_pereco_annual_fee',
  'company_pereco_fee_per_account',
  'company_publishing',
  'company_rejection_of_direct_debit_or_bank_transfer',
  'company_soles_annual_fee_tc',
  'company_soles_annual_reduced_fee_tc',
  'company_soles_fee_tc_per_affilliate',
  'company_soles_reduced_fee_tc_per_affilliate',
] as const;
const ZodB2bContractFeeLabel = z.enum(B2bContractFeeLabelValues);
export type B2bContractFeeLabel = z.infer<typeof ZodB2bContractFeeLabel>;

export const b2bContractFeeSettings: Record<
  B2bContractFeeLabel,
  {
    type: string;
    code: string;
  }
> = {
  affiliate_account_closure: {
    type: 'Versement',
    code: 'FS_cloture',
  },
  affiliate_account_position_at_given_date: {
    type: 'Facture',
    code: 'FS_relevé_position',
  },
  affiliate_collective_operation_by_cheque: {
    type: 'Versement',
    code: 'FS_règlement_part_int_ppv_chèque',
  },
  affiliate_direct_debit_rejection: {
    type: 'Versement',
    code: 'FS_impayé_rejet_prélèvement',
  },
  affiliate_estate_settlement_high_amount: {
    type: 'Versement',
    code: 'FS_succession_plus_5000',
  },
  affiliate_estate_settlement_low_amount: {
    type: 'Versement',
    code: 'FS_succession_500_5000',
  },
  affiliate_pledging_or_release_of_assets: {
    type: 'Facture',
    code: 'FS_nantissement',
  },
  affiliate_reissue_bank_transfer: {
    type: 'Versement',
    code: 'FS_réémission_virement',
  },
  affiliate_repayment_by_cheque: {
    type: 'Versement',
    code: 'FS_règlement_rachat_chèque',
  },
  affiliate_reissue_cheque: {
    type: 'Versement',
    code: 'FS_réémission_chèque',
  },
  affiliate_repayment_of_savings: {
    type: 'Versement',
    code: 'FS_rachat',
  },
  affiliate_repayment_reimbursement_triggered: {
    type: 'Versement',
    code: 'FS_rachat_seuil_déclenchement',
  },
  affiliate_retractation_following_pereco_payment: {
    type: 'Versement',
    code: 'FS_rétractation',
  },
  affiliate_search_for_documents_or_transactions_after_10_years: {
    type: 'Facture',
    code: 'FS_recherche_plus10',
  },
  affiliate_search_for_documents_or_transactions_before_10_years: {
    type: 'Facture',
    code: 'FS_recherche_moins10',
  },
  affiliate_statements_and_certificates: {
    type: 'Épargne',
    code: 'FS_édition_papier',
  },
  affiliate_transfer_from_pee_to_another_manager: {
    type: 'Versement',
    code: 'FS_transfert_sortant_PEE',
  },
  affiliate_transfer_from_pereco_to_another_manager_pct: {
    type: 'Versement',
    code: 'FS_transfert_sortant_PERECO',
  },
  affiliate_voluntary_payment_by_cheque: {
    type: 'Versement',
    code: 'FS_versement_chèque',
  },
  affiliate_annual_custody_for_former_or_retired_employees: {
    type: 'Épargne',
    code: 'FS_tenue_compte',
  },
  affiliate_annual_custody_for_former_or_retired_employees_pereco: {
    type: 'Épargne',
    code: 'FS_tenue_compte_PERECO',
  },
  affiliate_undelivered_mail_handling: {
    type: 'Épargne',
    code: 'FS_PND',
  },
  affiliate_specific_mail_requests_processing: {
    type: 'Épargne',
    code: 'FS_traitement_spécifique',
  },
  company_rejection_of_direct_debit_or_bank_transfer: {
    type: 'Facture',
    code: 'FE_impayé_rejet_prélèvement',
  },
  company_collective_transfer_to_another_manager: {
    type: 'Facture',
    code: 'FE_transfert_collectif',
  },
  company_file_opening: {
    type: 'Facture',
    code: 'FE_ouverture_dossier',
  },
  company_calculating_individual_allocations_particip: {
    type: 'Facture',
    code: 'FE_quotes_parts_participation',
  },
  company_calculating_individual_allocations_profit: {
    type: 'Facture',
    code: 'FE_quotes_parts_intéressement',
  },
  company_ccb_annual_fee: {
    type: 'Facture',
    code: 'FE_forfait_ccb',
  },
  company_ccb_fee_per_account: {
    type: 'Facture',
    code: 'FE_frais_ccb',
  },
  company_fee_per_account: {
    type: 'Facture',
    code: 'FE_frais_tc',
  },
  company_integration_of_affiliates_choices_via_mail_particip: {
    type: 'Facture',
    code: 'FE_bulletin_option_participation',
  },
  company_integration_of_affiliates_choices_via_mail_profit: {
    type: 'Facture',
    code: 'FE_bulletin_option_intéressement',
  },
  company_integration_of_affiliates_choices_via_mail_value: {
    type: 'Facture',
    code: 'FE_bulletin_option_ppv',
  },
  company_integration_of_affiliates_consultation_data_particip: {
    type: 'Facture',
    code: 'FE_intégration_fichier_participation',
  },
  company_integration_of_affiliates_consultation_data_profit: {
    type: 'Facture',
    code: 'FE_intégration_fichier_intéressement',
  },
  company_integration_of_affiliates_consultation_data_value: {
    type: 'Facture',
    code: 'FE_intégration_fichier_ppv',
  },
  company_integration_of_affiliates_data: {
    type: 'Facture',
    code: 'FE_intégration_fichier_affiliation',
  },
  company_other: {
    type: 'Facture',
    code: 'FE_frais_divers',
  },
  company_pee_fee_per_account: {
    type: 'Facture',
    code: 'FE_frais_tc_pee',
  },
  company_pee_pereco_annual_fee: {
    type: 'Facture',
    code: 'FE_forfait_pee_pereco',
  },
  company_pereco_fee_per_account: {
    type: 'Facture',
    code: 'FE_frais_tc_pereco',
  },
  company_publishing: {
    type: 'Facture',
    code: 'FE_frais_éditique',
  },
  company_soles_annual_fee_tc: {
    type: 'Facture',
    code: 'FE_soles_forfait_tc',
  },
  company_soles_annual_reduced_fee_tc: {
    type: 'Facture',
    code: 'FE_soles_forfait_tc_réduit',
  },
  company_soles_fee_tc_per_affilliate: {
    type: 'Facture',
    code: 'FE_soles_frais_tc',
  },
  company_soles_reduced_fee_tc_per_affilliate: {
    type: 'Facture',
    code: 'FE_soles_frais_tc_réduit',
  },
};

const ZodContractFeeCharge = z.object({
  id: ZodId,
  paid_by: ZodRecurringFeeChargePaidBy,
  b2b_contract_id: ZodId,
  b2b_company_id: ZodId,
  b2b_contract_version_num: z.number().int(),
  fee_label: ZodB2bContractFeeLabel,
  amount: z.number().int().min(0),
  created_at: z.date(),
  updated_at: z.date(),
});

export type ContractFeeChargeOne = z.infer<typeof ZodContractFeeCharge>;

export const postContractFeeChargesEndpoint = endpoint({
  method: 'POST',
  pathParams: undefined,
  queryParams: undefined,
  body: ZodContractFeeCharge.pick({
    paid_by: true,
    b2b_contract_id: true,
    fee_label: true,
    amount: true,
  }),
  responseBody: ZodContractFeeCharge.pick({ id: true }),
  headers: undefined,
});

export type PostContractFeeChargesEndpoint = GetEndpointType<typeof postContractFeeChargesEndpoint>;

export const getContractFeeChargesOneEndpoint = endpoint({
  method: 'GET',
  pathParams: ZodIDPathParam,
  responseBody: ZodContractFeeCharge,
});

export type GetContractFeeChargesOneEndpoint = GetEndpointType<
  typeof getContractFeeChargesOneEndpoint
>;

const ZodFilters = z
  .object({
    id: createZodArrayable(ZodId),
    b2b_contract_version_num: createZodArrayable(z.number().int()),
    from_created_at: createZodArrayable(ZodTrimmedString),
    to_created_at: createZodArrayable(ZodTrimmedString),
    b2b_company_id: createZodArrayable(ZodId),
    b2b_contract_id: createZodArrayable(ZodId),
    paid_by: createZodArrayable(ZodRecurringFeeChargePaidBy),
    fee_label: createZodArrayable(ZodB2bContractFeeLabel),
  })
  .partial();

export const getB2bContractFeeChargesSortColumnValues: (keyof ContractFeeChargeOne)[] = [
  'id',
  'paid_by',
  'b2b_contract_id',
  'b2b_company_id',
  'b2b_contract_version_num',
  'amount',
  'created_at',
  'updated_at',
];

const ZodGetContractFeeChargesQuery = buildZodBoListQueryParsed(
  ZodFilters,
  getB2bContractFeeChargesSortColumnValues,
  { range: [0, 100] },
);
export type GetContractFeeChargesQuery = z.infer<typeof ZodGetContractFeeChargesQuery>;
export const getContractFeeChargesEndpoint = endpoint({
  method: 'GET',
  responseBody: ZodContractFeeCharge.array(),
  queryParams: ZodGetContractFeeChargesQuery,
});

export type GetContractFeeChargesEndpoint = GetEndpointType<typeof getContractFeeChargesEndpoint>;
